/**
 * DataLayer - buttonClick event
 * @description по нажатию на любую кнопку срабатывает событие "buttonClick"
 * @description определенная кнопка может находится только в определенном блоке в ином случае ТС даст ошибку, по необходимости добавить нужным кнопкам placement
 *
 * @example DLClickEvent({ name: 'sticky', placement: 'header', eventLabel?: globalThis?.location?.href})
 *
 * @default event: "buttonClick"
 * @param name: Название кнопки в dataLayer
 * @param placement: Расположение кнопки в секции\блоке
 * @param eventLabel: Страница на которой произошло событие
 *
 */

import { pushToDL } from "../utils/helper"
import type { TDLPageURL } from "./type"

export type TDLButtonsVariant =
  | "sticky" // Стики кнопка
  | "click_cta" // CTA кнопка (оформить, получить и пр.)
  | "calc_sum_credit" // Калькулятор. Ползунок суммы
  | "loan_term_selection" // Калькулятор. Срок кредита
  | "monthly_payment" // Калькулятор. Ежемесячный платеж
  | "additional_ammount" // Калькулятор. Дополнительная сумма
  | "click_switcher_content" // Полезная информация, клик по табам
  | "click_qa" // Полезная информация, раскрытие блоков
  | "open_faq" // Вопросы и ответы
  | "other_offers" // Другие предложение по кредитам
  | "enter_esia" // Гос.услуги - войти
  | "more_details_esia" // Гос.услуги - подробнее
  | "phone_number" // Клик по номеру горячей линии
  | "view_products" // Экран дубля. Клик “Посмотреть товары
  | "go_back_to_the_main" // Экран отказы. Клик “Вернуться на главную
  | "refresh_the_page" // Экран ошибки. Клик “Обновить страницу
  | "logo" // Клик по логотипу
  | "timer" // Клик по таймеру
  | "tooltip" // Клик по тултипу
  | "userFeedbackTrigger" // Клик по виджету открытия
  | "userFeedbackVote" // Клик по выбору рейтинга
  | "returnPercentSwitcher" // Клик по свитчеру вернуть проценты
  | "enter_mobile" // Клик по кнопке авторизации MobileId

export type TDLEventAction = "on" | "off"

export type TDLPlacementBlocks =
  | "header"
  | "footer"
  | "banner"
  | "calculator"
  | "benefits"
  | "switcher_content"
  | "credit_delivery"
  | "faq"
  | "main"
  | "error_screen"
  | "userFeedbackWidget"

const buttonsPosition = {
  sticky: ["header", "banner"],

  click_cta: [
    "header",
    "footer",
    "banner",
    "calculator",
    "benefits",
    "switcher_content",
  ],

  calc_sum_credit: ["calculator"],
  loan_term_selection: ["calculator"],
  monthly_payment: ["calculator"],
  additional_ammount: ["calculator"],

  click_switcher_content: ["switcher_content"],
  click_qa: ["switcher_content"],

  open_faq: ["faq"],

  other_offers: ["footer"],

  enter_esia: ["main", "calculator"],
  enter_mobile: ["main"],
  more_details_esia: ["main"],

  phone_number: ["footer", "header"],

  view_products: ["error_screen"],
  go_back_to_the_main: ["error_screen"],
  refresh_the_page: ["error_screen"],

  logo: ["header"],
  timer: ["header"],
  tooltip: ["calculator"],
  userFeedbackTrigger: ["userFeedbackWidget"],
  userFeedbackVote: ["userFeedbackWidget"],
  returnPercentSwitcher: ["calculator"],
} satisfies Record<TDLButtonsVariant, TDLPlacementBlocks[]>

export const DLClickEvent = <
  TBtnName extends TDLButtonsVariant,
  TPlacement extends typeof buttonsPosition[TBtnName] extends Array<infer T>
    ? T
    : never
>({
  name,
  placement,
  eventLabel = globalThis?.location?.href,
}: {
  name: TBtnName
  placement: TPlacement
  eventLabel?: TDLPageURL
  eventAction?: TDLEventAction
}) => {
  pushToDL({ event: "buttonClick", name, placement, eventLabel })
}

export const DLClickEventOnce = (() => {
  // Замыкание для хранения уже выполненных событий
  const isExecuted: string[] = []

  return <
    TBtnName extends TDLButtonsVariant,
    TPlacement extends typeof buttonsPosition[TBtnName] extends Array<infer T>
      ? T
      : never
  >({
    name,
    placement,
    eventLabel = globalThis?.location?.href,
  }: {
    name: TBtnName
    placement: TPlacement
    eventLabel?: TDLPageURL
  }) => {
    // Проверяем, есть ли уже событие в списке выполненных
    if (!isExecuted.includes(name)) {
      isExecuted.push(name)
      pushToDL({
        event: "buttonClick",
        name,
        placement,
        eventLabel,
      })
    }
  }
})()

export const DLClickEventOnceWithAction = (() => {
  // Замыкание для хранения уже выполненных событий
  const isExecuted: { name: string; eventAction: string }[] = []

  return <
    TBtnName extends TDLButtonsVariant,
    TPlacement extends typeof buttonsPosition[TBtnName] extends Array<infer T>
      ? T
      : never
  >({
    name,
    placement,
    eventLabel = globalThis?.location?.href,
    eventAction,
  }: {
    name: TBtnName
    placement: TPlacement
    eventLabel?: TDLPageURL
    eventAction: TDLEventAction
  }) => {
    // Проверяем, есть ли уже событие в списке выполненных
    if (
      !isExecuted.some((el) => {
        const sameName = el.name === name
        const sameAction = el.eventAction === eventAction
        return sameName && sameAction
      })
    ) {
      isExecuted.push({ name, eventAction })
      pushToDL({
        event: "buttonClick",
        name,
        placement,
        eventLabel,
        eventAction,
      })
    }
  }
})()
